import React, { ReactNode } from 'react';
import { Flex } from 'glints-aries/es';
import { InlineError, TextInput, Typography } from 'glints-aries/es/@next';
import { Neutral } from 'glints-aries/es/@next/utilities/colors';
import { space4, space12 } from 'glints-aries/es/@next/utilities/spacing';
import { FieldPath, useController } from 'react-hook-form';
import styled from 'styled-components';

import {
  useCompanyVerificationFormContext,
  useIsViewOnlyMode,
} from '../FormContext';
import { CompanyVerificationFormState } from '../types';
import { FormContentStyles } from './styles.sc';

type TextFieldProps = {
  title: ReactNode;
  titleSuffix?: ReactNode;
  isRequired?: boolean;
  helperText?: ReactNode;
  fieldName: FieldPath<CompanyVerificationFormState>;
  placeholder?: string;
  fullWidth?: boolean;
  errorText?: string;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  disabled?: boolean;
};
const TextFieldContainer = styled.div<{ fullWidth: boolean }>`
  max-width: ${({ fullWidth }) => (fullWidth ? '100%' : '400px')};
`;
export default function TextField({
  title,
  titleSuffix,
  isRequired,
  helperText,
  fieldName,
  placeholder,
  fullWidth = false,
  errorText: errorTextProp,
  onChange: onChangeProp,
  onBlur: onBlurProp,
  disabled,
}: TextFieldProps) {
  const { control } = useCompanyVerificationFormContext();
  const {
    field: { onBlur, onChange, ref, value },
    fieldState: { error },
  } = useController({
    name: fieldName,
    control,
  });
  const isViewOnly = useIsViewOnlyMode();
  const errorText = errorTextProp || error?.message;

  return (
    <Flex flexDirection="column" gap={space12}>
      <Typography variant="body2" as="div">
        <Flex gap={space4} alignItems="center">
          <div>
            {title}
            {isRequired && <FormContentStyles.Asterisk />}
          </div>
          {titleSuffix}
        </Flex>
      </Typography>
      <Flex flexDirection="column" gap={space4}>
        <TextFieldContainer fullWidth={fullWidth}>
          <TextInput
            name={fieldName}
            value={(value || undefined) as string | undefined}
            onChange={onChangeProp || onChange}
            onBlur={() => {
              onBlur();
              onBlurProp?.();
            }}
            ref={ref}
            disabled={isViewOnly || disabled}
            placeholder={placeholder}
          />
        </TextFieldContainer>
        {errorText && <InlineError text={errorText} />}
        <If condition={helperText}>
          <Typography variant="subtitle2" color={Neutral.B40}>
            {helperText}
          </Typography>
        </If>
      </Flex>
    </Flex>
  );
}
